import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useUser } from "../context/UserContext";
import NotificationsSidebar from "./NotificationsSidebar";
import SearchModal from "./SearchModal";
import CreatePostModal from "./CreatePostModal";
import {
  HomeIcon,
  ReelsIcon,
  MessagesIcon,
  SearchIcon,
  ExploreIcon,
} from "./icons/AppIcons";

import {
  faHeart,
  faUser,
} from "@fortawesome/free-regular-svg-icons";

import {
  faSquarePlus,
  faBars,
  faGear,
} from "@fortawesome/free-solid-svg-icons";

import brandLogo from "../assets/brand logo.svg";
import "../styles/sidebar.css";
import { supabase } from "../lib/supabase";

function Sidebar() {
  const navigate = useNavigate();
  const { user, profile } = useUser();
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [hasUnreadNotif, setHasUnreadNotif] = useState(false);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  const avatarUrl = profile?.avatar_url;

  // ── Fetch unread notifications (just dot, no count) ──────────────────────
  const fetchUnreadNotif = useCallback(async () => {
    if (!profile?.id) return;
    const { data, error } = await supabase
      .from("notifications")
      .select("id")
      .eq("user_id", profile.id)
      .eq("is_read", false)
      .limit(1);
    if (!error) setHasUnreadNotif(data.length > 0);
  }, [profile]);

  // ── Fetch unread message count (number of conversations with unread msgs) ─
  const fetchUnreadMessages = useCallback(async () => {
    if (!user?.id) return;

    // Get all conversations this user is in
    const { data: convs } = await supabase
      .from("conversations")
      .select("id")
      .or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`);

    if (!convs?.length) { setUnreadMessageCount(0); return; }

    const convIds = convs.map(c => c.id);

    // Count distinct conversations that have at least one unread message
    // sent by the other person
    const { data: unreadMsgs } = await supabase
      .from("messages")
      .select("conversation_id")
      .in("conversation_id", convIds)
      .neq("sender_id", user.id)
      .eq("is_read", false);

    if (!unreadMsgs) { setUnreadMessageCount(0); return; }

    // Count unique conversations (not total messages)
    const uniqueConvs = new Set(unreadMsgs.map(m => m.conversation_id));
    setUnreadMessageCount(uniqueConvs.size);
  }, [user]);

  // Initial fetch
  useEffect(() => {
    fetchUnreadNotif();
    fetchUnreadMessages();
  }, [fetchUnreadNotif, fetchUnreadMessages]);

  // ── Realtime: listen for new messages ─────────────────────────────────────
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel("sidebar-messages-realtime")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "messages" },
        (payload) => {
          // Only bump if the new message is from someone else
          if (payload.new.sender_id !== user.id) {
            fetchUnreadMessages();
          }
        }
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "messages" },
        () => {
          // When messages are marked read, re-fetch to decrement
          fetchUnreadMessages();
        }
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [user, fetchUnreadMessages]);

  // ── Realtime: listen for new notifications ────────────────────────────────
  useEffect(() => {
    if (!profile?.id) return;

    const channel = supabase
      .channel("sidebar-notif-realtime")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${profile.id}`,
        },
        () => { setHasUnreadNotif(true); }
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [profile]);

  return (
    <>
      {/* SIDEBAR */}
      <aside className="sidebar">

        {/* TOP */}
        <div className="sidebar-top">
          <div className="menu-item logo">
            <img src={brandLogo} alt="CodeGram" className="brand-icon" />
          </div>
        </div>

        {/* MIDDLE */}
        <div className="sidebar-middle">

          <div className="menu-item" onClick={() => navigate("/feed")}>
            <HomeIcon />
            <span>Home</span>
          </div>

          <div className="menu-item" onClick={() => navigate("/posts")}>
            <ReelsIcon />
            <span>Posts</span>
          </div>

          {/* MESSAGES — badge with count */}
          <div className="menu-item" onClick={() => navigate("/messages")}>
            <div className="sidebar-icon-wrapper">
              <MessagesIcon />
              {unreadMessageCount > 0 && (
                <span className="sidebar-badge">
                  {unreadMessageCount > 99 ? "99+" : unreadMessageCount}
                </span>
              )}
            </div>
            <span>Messages</span>
          </div>

          <div className="menu-item" onClick={() => setShowSearch(true)}>
            <SearchIcon />
            <span>Search</span>
          </div>

          <div className="menu-item" onClick={() => navigate("/explore")}>
            <ExploreIcon />
            <span>Explore</span>
          </div>

          {/* NOTIFICATIONS — red dot only */}
          <div className="menu-item" onClick={() => setShowNotifications(true)}>
            <div className="sidebar-icon-wrapper">
              <FontAwesomeIcon icon={faHeart} />
              {profile?.push_notifications && hasUnreadNotif && (
                <span className="notif-red-dot" />
              )}
            </div>
            <span>Notifications</span>
          </div>

          <div className="menu-item" onClick={() => setShowCreatePost(true)}>
            <FontAwesomeIcon icon={faSquarePlus} />
            <span>Create</span>
          </div>

          {/* PROFILE */}
          <div className="menu-item" onClick={() => navigate("/profile")}>
            {avatarUrl ? (
              <img src={avatarUrl} alt="Profile" className="sidebar-avatar" />
            ) : (
              <FontAwesomeIcon icon={faUser} />
            )}
            <span>Profile</span>
          </div>

        </div>

        {/* BOTTOM */}
        <div className="sidebar-bottom">
          <div className="menu-item" onClick={() => setShowMoreMenu(true)}>
            <FontAwesomeIcon icon={faBars} />
            <span>More</span>
          </div>
        </div>
      </aside>

      <NotificationsSidebar
        open={showNotifications}
        onClose={() => {
          setShowNotifications(false);
          fetchUnreadNotif();
        }}
      />

      <CreatePostModal
        open={showCreatePost}
        onClose={() => setShowCreatePost(false)}
        onSelectFile={() => { alert("File picker will open here"); }}
      />

      <SearchModal open={showSearch} onClose={() => setShowSearch(false)} />

      {/* MORE MENU */}
      {showMoreMenu && (
        <>
          <div className="more-menu-overlay" onClick={() => setShowMoreMenu(false)} />
          <div className="more-menu">
            <div
              className="more-menu-item"
              onClick={() => { setShowMoreMenu(false); navigate("/settings"); }}
            >
              <FontAwesomeIcon icon={faGear} />
              <span>Settings & Privacy</span>
            </div>
            <div className="more-menu-divider" />
            <div
              className="more-menu-item logout"
              onClick={async () => {
                await supabase.auth.signOut();
                setShowMoreMenu(false);
                navigate("/");
              }}
            >
              <FontAwesomeIcon icon={faBars} />
              <span>Log out</span>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default Sidebar;
